import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CodaltComponent } from '../codalt.component';
import { Checkpoint } from '../classes/checkpoint';
import { CheckpointService } from '../services/checkpoint.service';
import { CheckpointResult } from '../classes/checkpoint-result';
import { DatePipe } from '@angular/common';
import { CheckpointResultLevelStatusPipe } from '../pipes/checkpoint-result-level-status.pipe';
import { PlatformService } from '../services/platform.service';
import { Platform } from '../classes/platform';
import { CheckpointResultService } from '../services/checkpoint-result.service';
import { BreadcrumbComponent } from '../services/breadcrumb/breadcrumb.component';
import { Routenames } from '../classes/route-name.enum';
import { DurationPipe } from '../pipes/duration.pipe';
import { CheckpointHeaderComponent } from '../services/checkpoint-header/checkpoint-header.component';
import { BytesPipePipe } from '../pipes/bytes.pipe';
import { User } from '../classes/user.class';
import { CheckpointComment } from '../classes/checkpoint-comment';
import { CheckpointCommentService } from '../services/checkpoint-comment.service';
import { CodaltDatePipe } from '../pipes/codalt-date.pipe';
import { MatFormField, MatInput } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Utils } from '../utils.class';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MsBetweenPipe } from '../pipes/ms-between.pipe';
import { MatTooltip } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-checkpoint-overview',
    templateUrl: './checkpoint-overview.component.html',
    styleUrls: ['./checkpoint-overview.component.scss'],
    imports: [
        DatePipe,
        RouterLink,
        CheckpointResultLevelStatusPipe,
        BreadcrumbComponent,
        DurationPipe,
        CheckpointHeaderComponent,
        BytesPipePipe,
        CodaltDatePipe,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatIcon,
        MatIconButton,
        MsBetweenPipe,
        MatTooltip,
    ],
})
export class CheckpointOverviewComponent extends CodaltComponent implements OnInit {
    readonly platform = signal<Platform>(new Platform());
    readonly checkpoint = signal<Checkpoint>(new Checkpoint());
    readonly checkpointResults = signal<null | CheckpointResult[]>(null);
    readonly checkpointComments = signal<null | CheckpointComment[]>(null);

    users: User[] = [];
    platformId: number;

    readonly successfulPush = signal<boolean>(false);
    readonly failedPush = signal<boolean>(false);

    readonly formCreateComment = signal<null | FormGroup>(null);

    // Subscriptions of the current route params, replaced when navigating to another checkpoint
    private routeSubscriptions = new Subscription();

    readonly isPostponed = computed(() => {
        const postponedTill = this.checkpoint()?.postponed_till;
        return !!postponedTill && new Date(postponedTill) > new Date();
    });

    constructor(
        private activatedRoute: ActivatedRoute,
        private checkpointCommentService: CheckpointCommentService,
        private checkpointResultService: CheckpointResultService,
        private checkpointService: CheckpointService,
        private confirmDialog: ConfirmDialogService,
        private platformService: PlatformService,
        private titleService: Title,
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { platformId; checkId }) => {
                this.platformId = +params.platformId;
                this.routeSubscriptions.unsubscribe();
                this.routeSubscriptions = new Subscription();
                this.subscriptions.add(this.routeSubscriptions);
                this.checkpointResults.set(null);

                this.routeSubscriptions.add(
                    this.platformService
                        .liveItem(
                            +params.platformId,
                            this.platformService.getPlatform(+params.platformId),
                        )
                        .subscribe({
                            next: (platform) => {
                                this.platform.set(platform);
                                this.setCrumbs();
                                this.titleService.setTitle(
                                    `${this.platform().name} - Seire ${this.environment.titleAppend}`,
                                );
                            },
                            error: (err) => console.error(err),
                        }),
                );

                this.routeSubscriptions.add(
                    this.checkpointService
                        .liveItem(
                            +params.checkId,
                            this.checkpointService.getCheckpoint(+params.checkId),
                        )
                        .subscribe({
                            next: (checkpoint) => {
                                this.checkpoint.set(checkpoint);
                                this.setCrumbs();
                            },
                            error: (err) => console.error(err),
                        }),
                );

                this.routeSubscriptions.add(
                    this.checkpointResultService
                        .liveList(
                            this.checkpointResultService.getCheckpointResults(+params.checkId),
                            (checkpointResult) =>
                                +checkpointResult.platform_check_id === +params.checkId,
                        )
                        .subscribe({
                            // Pushed results are appended, newest first matches the order of the API
                            next: (checkpointResults) =>
                                this.checkpointResults.set(
                                    [...checkpointResults].sort((a, b) => b.id - a.id),
                                ),
                            error: (err) => console.error(err),
                        }),
                );

                this.routeSubscriptions.add(
                    this.checkpointCommentService.getCheckpointComments(+params.checkId).subscribe({
                        next: (checkpointComments) => {
                            this.checkpointComments.set(checkpointComments);
                        },
                        error: (err) => console.error(err),
                    }),
                );

                this.createForm(+params.checkId);
            }),
        );
    }

    setCrumbs() {
        this.crumbs = [
            { name: 'Seire', route: Routenames.seire },
            {
                name: `Platform : ${this.platform().name}`,
                route: `${Routenames.seire}/${this.platform().id}`,
            },
            {
                name: `Checkpoint : ${this.checkpoint().name}`,
                route: `${Routenames.seire}/${this.platform().id}/overview/${this.checkpoint().id}`,
            },
        ];
    }

    createForm(checkpointId: number) {
        const checkpointComment = new CheckpointComment();

        this.formCreateComment.set(
            new FormGroup({
                id: new FormControl(checkpointComment.id),
                checkpoint_id: new FormControl(checkpointId),
                postpone_hours: new FormControl(
                    checkpointComment.postpone_hours,
                    Validators.max(72),
                ),
                comment: new FormControl(checkpointComment.comment, Validators.required),
                updated_at: new FormControl(checkpointComment.updated_at),
            }),
        );
    }

    save(providedForm: FormGroup) {
        Utils.triggerValidation(providedForm);
        if (providedForm.valid) {
            const checkpointComment = new CheckpointComment();
            Object.assign(checkpointComment, providedForm.value);

            this.subscriptions.add(
                this.checkpointCommentService.saveCheckpointComment(checkpointComment).subscribe({
                    next: (savedComment) => {
                        this.checkpointComments.set([
                            savedComment?.data,
                            ...this.checkpointComments(),
                        ]);
                        this.createForm(this.checkpoint().id);
                    },
                    error: (err) => console.error(err),
                }),
            );
        }
    }

    deleteComment(event: PointerEvent, providedCheckpointComment: CheckpointComment) {
        event.preventDefault();
        event.stopPropagation();

        const checkpointComment = new CheckpointComment();
        Object.assign(checkpointComment, providedCheckpointComment);

        this.confirmDialog
            .confirm(
                'Verwijderen',
                `Weet u zeker dat u de opmerking wilt verwijderen?`,
                'Verwijderen',
                'Annuleren',
            )
            .then(() => {
                this.checkpointCommentService
                    .deleteCheckpointComment(checkpointComment.id)
                    .then(() => {
                        this.checkpointComments.update((checkpointComments) =>
                            checkpointComments.filter(
                                (comment) => comment.id !== checkpointComment.id,
                            ),
                        );
                    });
            })
            .catch(() => {
                console.log('checkpoint comment not deleted');
            });
    }

    queueImmediately() {
        this.subscriptions.add(
            this.checkpointService.queueImmediately(this.checkpoint().id).subscribe({
                next: (removedCheckpointResult) => {
                    this.checkpointResults.update((checkpointResults) =>
                        checkpointResults.filter(
                            (checkpointResult) =>
                                checkpointResult.id !== removedCheckpointResult.data.id,
                        ),
                    );
                    this.successfulPush.set(true);
                    setTimeout(() => {
                        this.successfulPush.set(false);
                    }, 2500);
                },
                error: (err) => {
                    console.error(err);
                    this.failedPush.set(true);
                    setTimeout(() => {
                        this.failedPush.set(false);
                    }, 2500);
                },
            }),
        );
    }
}
